import { Apple, Download, Globe, Monitor, Terminal } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { DOWNLOADS_SECTION_ID, type PlatformDownload, type PlatformDownloadId } from '@/config/downloads';
import { useLocalizedDownloads } from '@/hooks/use-localized-downloads';
import { useLocale } from '@/i18n/locale-context';
import { cn } from '@/lib/utils';

const iconById: Record<PlatformDownloadId, LucideIcon> = {
  windows: Monitor,
  'macos-arm64': Apple,
  'macos-x64': Apple,
  linux: Terminal,
  web: Globe,
};

function PlatformCard({
  item,
  index,
  hoverAction,
}: {
  item: PlatformDownload;
  index: number;
  hoverAction: string;
}) {
  const Icon = iconById[item.id];

  return (
    <Button
      variant="outline"
      size="lg"
      className={cn(
        'group card-shimmer relative h-auto min-h-[4rem] w-full min-w-0 overflow-hidden border-0 p-0',
        'bg-card shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-lg',
        'before:absolute before:top-0 before:left-1/2 before:-translate-x-1/2 before:h-[2px] before:w-12 before:bg-gradient-to-r before:from-transparent before:via-primary/50 before:to-transparent',
        'active:scale-[0.97] transition-transform duration-150',
        'animate-fade-up opacity-0 [animation-fill-mode:forwards]'
      )}
      style={{ animationDelay: `${index * 80 + 280}ms` }}
      asChild
    >
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${item.label}, ${item.hint}`}
        className="relative block min-h-[4rem] w-full px-4 py-4 sm:px-5"
      >
        <div className="flex min-h-[3rem] flex-col items-start justify-center gap-1.5 pr-6 transition-opacity duration-200 group-hover:opacity-0 group-focus-visible:opacity-0">
          <span className="flex items-center gap-2">
            <Icon className="h-4 w-4 shrink-0 opacity-80 transition-transform duration-300 group-hover:scale-110" strokeWidth={1.75} />
            <span className="text-sm font-medium sm:text-[15px]">{item.label}</span>
          </span>
          <span className="max-w-full text-xs font-normal leading-snug text-muted-foreground">
            {item.hint}
          </span>
        </div>

        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-foreground opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100">
          <Download className="h-5 w-5 opacity-90" strokeWidth={1.75} aria-hidden />
          <span className="text-sm font-semibold tracking-tight">{hoverAction}</span>
        </div>

        {item.id === 'macos-arm64' ? (
          <span className="pointer-events-none absolute right-2.5 top-2.5 rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
            Recommended
          </span>
        ) : null}
      </a>
    </Button>
  );
}

export function PlatformDownloads() {
  const downloads = useLocalizedDownloads();
  const { t } = useLocale();

  return (
    <div
      id={DOWNLOADS_SECTION_ID}
      className="mx-auto grid w-full max-w-7xl scroll-mt-24 grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-5"
    >
      {downloads.map((item, index) => (
        <PlatformCard
          key={item.id}
          item={item}
          index={index}
          hoverAction={
            item.id === 'web' ? t.platformsHover.open : t.platformsHover.download
          }
        />
      ))}
    </div>
  );
}
